import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { PlusIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EntitySheet } from "@/components/entity-sheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error — legacy JS, no type defs.
import AutocompleteInput from "../../autocomplete-input";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error — legacy JS, no type defs.
import DynamicFieldSelectorDialog from "../../dynamic-field-selector";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error — legacy JS, no type defs.
import DynamicFieldValueComponent from "../../dynamic-field-value";
import { TagsMultiSelect } from "../../tags-multi-select";
import { useNotificationsProvider } from "../../../context/NotificationsContext";
import {
  useEditResourceMutation,
  useGetTagsForEntityQuery,
  useSetTagsForEntityMutation,
} from "../../../services/api";

interface TagRef {
  id: number;
  name: string;
  color: string | null;
}

interface DynamicField {
  title: string;
  dataType: string;
  value: unknown;
}

export interface ResourceRowForSheet {
  id: string | number;
  name?: string;
  tags?: TagRef[];
  dynamicData?: DynamicField[];
  [key: string]: unknown;
}

interface ResourceSheetProps {
  value: ResourceRowForSheet | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canEdit: boolean;
  /**
   * The sheet is open but the row hasn't been resolved yet — e.g. arriving via
   * a deep link (search) before the resource list has loaded. Shows skeletons
   * instead of an empty panel until `value` arrives.
   */
  loading?: boolean;
  initialEditing?: boolean;
  onEdited?: () => void;
  onDelete?: () => void;
}

interface FormValues {
  name: string;
  selectedTags: TagRef[];
  dynamicFields: DynamicField[];
}

export function ResourceSheet({
  value,
  open,
  onOpenChange,
  canEdit,
  loading = false,
  initialEditing = false,
  onEdited,
  onDelete,
}: ResourceSheetProps) {
  const { t } = useTranslation();
  const { showNotification } = useNotificationsProvider();

  const [editResource, { isLoading: isEditing }] = useEditResourceMutation();
  const [setTags, { isLoading: isSettingTags }] = useSetTagsForEntityMutation();

  // Server is the source of truth for the current tag set — the row from
  // the list query may be stale after another tab edited the resource.
  const { data: currentTags } = useGetTagsForEntityQuery(
    { taggableType: "Resource", taggableId: value?.id },
    { skip: !open || !value?.id },
  ) as { data?: TagRef[] };

  const [editing, setEditing] = useState(false);
  const [openDynamicFieldSelector, setOpenDynamicFieldSelector] =
    useState(false);
  const [selectedDynamicField, setSelectedDynamicField] =
    useState<DynamicField | null>(null);

  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1, t("resource.errors.name-required", "Name is required")),
        selectedTags: z.array(z.any()),
        dynamicFields: z.array(z.any()),
      }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: value?.name ?? "",
      selectedTags: value?.tags ?? [],
      dynamicFields: value?.dynamicData ?? [],
    },
  });

  // Reset the form when the upstream row changes or the sheet (re)opens.
  useEffect(() => {
    if (!value) return;
    form.reset({
      name: value.name ?? "",
      selectedTags: value.tags ?? [],
      dynamicFields: value.dynamicData ?? [],
    });
    setEditing(open ? initialEditing : false);
    // `form` is intentionally excluded: including it would cause a reset loop
    // because form.reset() itself triggers re-renders. Re-seed only when the
    // displayed entity changes or the sheet opens/closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.id, open, initialEditing]);

  // Hydrate the selected tag list from the dedicated GET. Falls back to
  // the row's tags array while the request is in flight.
  useEffect(() => {
    if (Array.isArray(currentTags)) {
      form.setValue(
        "selectedTags",
        currentTags.map((tag) => ({
          id: tag.id,
          name: tag.name,
          color: tag.color,
        })),
      );
    } else if (value?.tags) {
      form.setValue("selectedTags", value.tags);
    }
    // `form` is intentionally excluded — same reset-loop reason as above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTags, value?.tags]);

  function onFieldAdded(data: DynamicField) {
    setOpenDynamicFieldSelector(false);
    const current = form.getValues("dynamicFields");
    if (selectedDynamicField) {
      form.setValue(
        "dynamicFields",
        current.map((field) => (field === selectedDynamicField ? data : field)),
      );
      setSelectedDynamicField(null);
      return;
    }
    form.setValue("dynamicFields", [...current, data]);
  }

  function onEditField(data: DynamicField) {
    setSelectedDynamicField(data);
    setOpenDynamicFieldSelector(true);
  }

  function onDeleteField(data: DynamicField) {
    const current = form.getValues("dynamicFields");
    form.setValue(
      "dynamicFields",
      current.filter((field) => field !== data),
    );
  }

  const onSubmit = async (values: FormValues) => {
    if (!value) return;
    try {
      await editResource({
        id: value.id,
        name: values.name.trim(),
        dynamicData: values.dynamicFields,
      }).unwrap();
      await setTags({
        taggableType: "Resource",
        taggableId: value.id,
        tagIds: values.selectedTags.map((tag) => tag.id),
      }).unwrap();
      setEditing(false);
      onEdited?.();
    } catch {
      showNotification("error", t("common.api-error"));
    }
  };

  const saving = isEditing || isSettingTags;
  const dynamicFieldsForRead = form.watch("dynamicFields");
  const tagsForRead = form.watch("selectedTags");

  return (
    <>
      <EntitySheet
        open={open}
        onOpenChange={onOpenChange}
        title={
          loading ? (
            <Skeleton className="h-5 w-40" />
          ) : (
            value?.name ?? t("sidebar.resources")
          )
        }
        editing={editing}
        // Hide the read/edit toggle and delete affordance until the row loads —
        // there's nothing to act on yet.
        onToggleEdit={loading ? undefined : setEditing}
        canEdit={canEdit}
        onDelete={loading ? undefined : onDelete}
        footer={
          editing && !loading ? (
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setEditing(false)}
                disabled={saving}
              >
                {t("common.cancel")}
              </Button>
              <Button onClick={form.handleSubmit(onSubmit)} disabled={saving}>
                {t("common.save")}
              </Button>
            </div>
          ) : null
        }
      >
        {loading ? (
          <ResourceReadSkeleton />
        ) : editing ? (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-5"
              noValidate
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>
                      {t("resource.add-resource-dialog.name-title")}
                    </FormLabel>
                    <FormControl>
                      <AutocompleteInput
                        error={fieldState.error ? "required" : undefined}
                        onChange={(val: string) => field.onChange(val)}
                        defaultValue={
                          value && { id: value.id, title: value.name }
                        }
                        resource="resource/name-autocomplete"
                        required
                        canDelete
                        refreshOnInputChange
                        clearOnBlur={false}
                        freeSolo
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="selectedTags"
                render={({ field }) => (
                  <FormItem>
                    <TagsMultiSelect
                      value={field.value}
                      onChange={field.onChange}
                      label={t("resource.add-resource-dialog.tags-title")}
                      placeholder={t("tags.multi-select.placeholder")}
                    />
                  </FormItem>
                )}
              />

              {dynamicFieldsForRead.length > 0 ? (
                <div className="space-y-3">
                  {dynamicFieldsForRead.map((field) => (
                    <DynamicFieldValueComponent
                      key={`${field.title}-${field.dataType}`}
                      value={field}
                      onEdit={onEditField}
                      onDelete={onDeleteField}
                    />
                  ))}
                </div>
              ) : null}

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setOpenDynamicFieldSelector(true)}
                className="text-primary"
              >
                <PlusIcon className="size-4" />
                {t("resource.add-resource-dialog.add-field")}
              </Button>
            </form>
          </Form>
        ) : (
          <ResourceReadView
            tags={tagsForRead}
            dynamicFields={dynamicFieldsForRead}
            t={t}
          />
        )}
      </EntitySheet>

      <DynamicFieldSelectorDialog
        open={openDynamicFieldSelector}
        onClose={() => {
          setSelectedDynamicField(null);
          setOpenDynamicFieldSelector(false);
        }}
        onCreated={onFieldAdded}
        title={t("components.dynamic-field-selector.title")}
        value={selectedDynamicField}
      />
    </>
  );
}

/** Placeholder mirroring ResourceReadView (tags + fields) while the row loads. */
function ResourceReadSkeleton() {
  return (
    <div className="space-y-5" aria-busy="true">
      <section>
        <Skeleton className="mb-2 h-3 w-16" />
        <div className="flex flex-wrap gap-1.5">
          <Skeleton className="h-5 w-16 rounded-full" />
          <Skeleton className="h-5 w-12 rounded-full" />
        </div>
      </section>
      <section>
        <Skeleton className="mb-2 h-3 w-24" />
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="flex items-baseline justify-between gap-3 border-b border-border/40 pb-2 last:border-0"
            >
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function ResourceReadView({
  tags,
  dynamicFields,
  t,
}: {
  tags: TagRef[];
  dynamicFields: DynamicField[];
  t: ReturnType<typeof useTranslation>["t"];
}) {
  return (
    <div className="space-y-5">
      <section>
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t("resource.items.tags")}
        </h3>
        {tags.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag) => (
              <Badge
                key={tag.id}
                style={{
                  backgroundColor: tag.color ?? undefined,
                  color: tag.color ? "#fff" : undefined,
                }}
              >
                {tag.name}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t("tags.empty")}</p>
        )}
      </section>

      {dynamicFields.length > 0 ? (
        <section>
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("resource.see-resource.resource-id")}
          </h3>
          <dl className="space-y-2">
            {dynamicFields.map((field) => (
              <div
                key={`${field.title}-${field.dataType}`}
                className="flex items-baseline justify-between gap-3 border-b border-border/40 pb-2 last:border-0"
              >
                <dt className="text-sm text-muted-foreground">{field.title}</dt>
                <dd className="text-sm">{String(field.value ?? "")}</dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}
    </div>
  );
}
